/**************************************************/
/* Methods for PCA and PCVPCA based on wasm code  */
/**************************************************/

import { ismatrix, Matrix } from 'mdatools/arrays';


/**
 * Returns length of array with PCA results.
 *
 * @param {number} nrows number of rows in a dataset (without outliers).
 * @param {number} ncomp number of components in the model.
 * @param {number} ncols number of columns in a dataset (without excluded variables).
 *
 * @returns {number} the length of the results array.
 */
export function pca_getreslength(nrows, ncomp, ncols) {

   // Structure of PCA results:
   // - T: nr x ncomp
   // - H: nr x ncomp
   // - Q: nr x ncomp
   // - expvar: 1 x ncomp
   // - Fc: nr x ncomp
   // - Fr: nr x ncomp

   // - G: nc x ncomp
   // - R: nc x ncomp
   // - Ic: nc x ncomp
   // - Ir: nc x ncomp

   return (5 * nrows * ncomp) + ncomp + (4 * ncols * ncomp);
}


/**
 * Returns length of array with PCA model.
 *
 * @param {number} ncols number of columns in calibration set (without excluded).
 * @param {number} ncomp number of components
 *
 * @returns {number} the length of the model array.
 */
export function pca_getmodellength(ncols, ncomp) {

   // Structure of PCA model:
   // - nc and ncomp: 2
   // - mX: 1 x ncols
   // - sX: 1 x ncols
   // - P: ncols x ncomp
   // - eigenvals: 1 x ncomp
   // - cq0: 1 x ncomp
   // - cNq: 1 x ncomp
   // - rq0: 1 x ncomp
   // - rNq: 1 x ncomp
   // - ch0: 1 x ncomp
   // - cNh: 1 x ncomp
   // - rh0: 1 x ncomp
   // - rNh: 1 x ncomp

   // - cr0: 1 x ncomp
   // - cNr: 1 x ncomp
   // - rr0: 1 x ncomp
   // - rNr: 1 x ncomp
   // - cg0: 1 x ncomp
   // - cNg: 1 x ncomp
   // - rg0: 1 x ncomp
   // - rNg: 1 x ncomp

   return 2 + 2 * ncols + ncols * ncomp + 17 * ncomp;
}


/**
 * Returns part of PCA model array for given outcome.
 *
 * @param {TypedArray} m array with PCA model.
 * @param {string} name name of the outcome (e.g. 'mX', 'sX', 'loadings', etc.).
 * @param {number} a component number (for loadings).
 *
 * @returns {TypedArray} array with outcomes (by reference).
 */
export function pcamodel_get(m, name, a) {

   if (!m || !m.class || !m.class.includes('pcamodel')) {
      throw new Error('pcamodel_get: parameter "m" must be JSON with PCA model.');
   }

   const ncols = m.ncols;
   const ncomp = m.ncomp;
   let offset, length;

   switch(name) {
      case 'mX':
         offset = 2;
         length = ncols;
         break;
      case 'sX':
         offset = 2 + ncols;
         length = ncols;
         break;
      case 'loadings':
      case 'xloadings':
         offset = 2 + 2 * ncols + (a - 1) * ncols;
         length = ncols;
         break;
      case 'P':
         offset = 2 + 2 * ncols;
         length = a * ncols;
         break;
      case 'eigenvals':
      case 'xeigenvals':
         offset = 2 + 2 * ncols + ncols * ncomp;
         length = ncomp;
         break;
      case 'cq0':
         offset = 2 + 2 * ncols + ncols * ncomp + ncomp;
         length = ncomp;
         break;
      case 'cNq':
         offset = 2 + 2 * ncols + ncols * ncomp + 2 * ncomp;
         length = ncomp;
         break;
      case 'rq0':
         offset = 2 + 2 * ncols + ncols * ncomp + 3 * ncomp;
         length = ncomp;
         break;
      case 'rNq':
         offset = 2 + 2 * ncols + ncols * ncomp + 4 * ncomp;
         length = ncomp;
         break;
      case 'ch0':
         offset = 2 + 2 * ncols + ncols * ncomp + 5 * ncomp;
         length = ncomp;
         break;
      case 'cNh':
         offset = 2 + 2 * ncols + ncols * ncomp + 6 * ncomp;
         length = ncomp;
         break;
      case 'rh0':
         offset = 2 + 2 * ncols + ncols * ncomp + 7 * ncomp;
         length = ncomp;
         break;
      case 'rNh':
         offset = 2 + 2 * ncols + ncols * ncomp + 8 * ncomp;
         length = ncomp;
         break;
      case 'cr0':
         offset = 2 + 2 * ncols + ncols * ncomp + 9 * ncomp;
         length = ncomp;
         break;
      case 'cNr':
         offset = 2 + 2 * ncols + ncols * ncomp + 10 * ncomp;
         length = ncomp;
         break;
      case 'rr0':
         offset = 2 + 2 * ncols + ncols * ncomp + 11 * ncomp;
         length = ncomp;
         break;
      case 'rNr':
         offset = 2 + 2 * ncols + ncols * ncomp + 12 * ncomp;
         length = ncomp;
         break;
      case 'cg0':
         offset = 2 + 2 * ncols + ncols * ncomp + 13 * ncomp;
         length = ncomp;
         break;
      case 'cNg':
         offset = 2 + 2 * ncols + ncols * ncomp + 14 * ncomp;
         length = ncomp;
         break;
      case 'rg0':
         offset = 2 + 2 * ncols + ncols * ncomp + 15 * ncomp;
         length = ncomp;
         break;
      case 'rNg':
         offset = 2 + 2 * ncols + ncols * ncomp + 16 * ncomp;
         length = ncomp;
         break;

      default:
         throw Error('pcamodel_get: wrong outcome name.');
   }

   return m.v.subarray(offset, offset + length);
}


/**
 * Returns part of PCA result array for given outcome.
 *
 * @param {TypedArray} r array with PCA results.
 * @param {string} name name of the outcome (e.g. 'scores', 'hdist', 'qdist', 'expvar').
 * @param {number} a component number (for scores and distances).
 *
 * @returns {TypedArray} array with outcomes (by reference).
 */
export function pcares_get(r, name, a) {

   if (!r || !r.class || !r.class.includes('pcares')) {
      throw new Error('pcares_get: parameter "r" must be JSON with PCA results.');
   }

   const ncols = r.ncols;
   const nrows = r.nrows;
   const ncomp = r.ncomp;
   let offset, length;

   switch(name) {
      case 'scores':
      case 'xscores':
         offset = (a - 1) * nrows;
         length = nrows;
         break;
      case 'hdist':
         offset = nrows * ncomp + (a - 1) * nrows;
         length = nrows;
         break;
      case 'qdist':
         offset = 2 * nrows * ncomp + (a - 1) * nrows;
         length = nrows;
         break;
      case 'xcumexpvar':
      case 'cumexpvar':
         offset = 3 * nrows * ncomp;
         length = ncomp;
         break;
      case 'fdistc':
         offset = 3 * nrows * ncomp + ncomp + (a - 1) * nrows;
         length = nrows;
         break;
      case 'fdistr':
         offset = 4 * nrows * ncomp + ncomp + (a - 1) * nrows;
         length = nrows;
         break;

      case 'gdist':
         offset = 5 * nrows * ncomp + ncomp + (a - 1) * ncols;
         length = ncols;
         break;
      case 'rdist':
         offset = (5 * nrows * ncomp) + ncomp + (ncols * ncomp) + (a - 1) * ncols;
         length = ncols;
         break;
      case 'idistc':
         offset = (5 * nrows * ncomp) + ncomp + (2 * ncols * ncomp) + (a - 1) * ncols;
         length = ncols;
         break;
      case 'idistr':
         offset = (5 * nrows * ncomp) + ncomp + (3 * ncols * ncomp) + (a - 1) * ncols;
         length = ncols;
         break;

      default:
         throw new Error('pcares_get: wrong outcome name.');
   }

   return r.v.subarray(offset, offset + length);
}

function isArrCorrect(arr, minVal, maxVal) {
   if (!Array.isArray(arr)) return false;
   if (arr.length === 0) return true;

   for (let i = 0; i < arr.length; i++) {
      const v = arr[i];
      if (v < minVal || v > maxVal) return false;
   };

   return true;
}

/**
 * Creates a PCA model using wasm implementation.
 *
 * @param {Object} Module object with wasm module for PCVPCA.
 * @param {Matrix} X matrix with calibration set.
 * @param {number} ncomp number of components.
 * @param {boolean} center mean center the data columns or not.
 * @param {boolean} scale standardize the data columns or not.
 * @param {Array} outliers array with indices of outliers.
 * @param {Array} exclvars array with indices of excluded variables.
 *
 * @returns {Object} JSON with PCA model.
 */
export function pca_fit(Module, X, ncomp, center, scale, outliers, exclvars) {

   // check the dataset
   if (!ismatrix(X)) {
      throw new Error('pca_fit: X must be a matrix.');
   }

   // process outliers
   if (!outliers) outliers = [];
   const nout = outliers && Array.isArray(outliers) ? outliers.length : 0;
   if (!isArrCorrect(outliers, 0, X.nrows - 1)) {
      throw new Error('pca_fit: some indices in "outliers" array are outside the expected range.');
   }

   // process excluded variables
   if (!exclvars) exclvars = [];
   const nexv = exclvars && Array.isArray(exclvars) ? exclvars.length : 0;
   if (!isArrCorrect(exclvars, 0, X.ncols - 1)) {
      throw new Error('pca_fit: some indices in "exclvars" array are outside the expected range.');
   }

   const nrows = X.nrows - nout;
   const ncols = X.ncols - nexv;

   if (nrows < 3 || ncols < 2) {
      throw new Error('pca_fit: X must have at least 3 rows and 2 columns.');
   }


   // set the default values
   if (center === undefined) {
      center = true;
   }

   if (scale === undefined) {
      scale = false;
   }

   if (ncomp === undefined) {
      ncomp = Math.min(X.nrows - nout - 1, X.ncols - nexv);
   }

   if (ncomp < 1 || ncomp > Math.min(nrows - 1, ncols)) {
      throw new Error('pca_fit: the value of "ncomp" does not match the dimension of X.');
   }


   // allocate memory in WASM heap space and copy dataset values
   const x_ptr = Module._malloc(X.v.length * X.v.BYTES_PER_ELEMENT);
   Module.HEAPF64.set(X.v, x_ptr / X.v.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space and copy outliers values
   let o_ptr = null;
   if (nout > 0) {
      const o = new Int32Array(outliers);
      o_ptr = Module._malloc(o.length * o.BYTES_PER_ELEMENT);
      Module.HEAP32.set(o, o_ptr / o.BYTES_PER_ELEMENT);
   }

   // allocate memory in WASM heap space and copy excluded variables values
   let ev_ptr = null;
   if (nexv > 0) {
      const ev = new Int32Array(exclvars);
      ev_ptr = Module._malloc(ev.length * ev.BYTES_PER_ELEMENT);
      Module.HEAP32.set(ev, ev_ptr / ev.BYTES_PER_ELEMENT);
   }

   // allocate memory in WASM heap space for model and calibration set results outcomes
   const m_length = pca_getmodellength(ncols, ncomp);
   const m_constructor = Float64Array;
   const m_ptr = Module._malloc(m_length * m_constructor.BYTES_PER_ELEMENT);

   // call WASM function for PCA
   const res = Module.ccall('pcafit_wasm',
               ['number'], // return type
               ['number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'], // argument types
               [x_ptr, X.nrows, X.ncols, ncomp, center, scale, m_ptr, o_ptr, nout, ev_ptr, nexv]); // arguments

   if (res !== 1) {
      throw new Error('pca_fit: PCA failed.');
   }

   // copy part of HEAP memory with model outcomes
   const model = Module.HEAPF64.slice(
      m_ptr/m_constructor.BYTES_PER_ELEMENT,
      m_ptr/m_constructor.BYTES_PER_ELEMENT + m_length
   );

   // free the memory
   Module._free(x_ptr);
   Module._free(m_ptr);
   if (o_ptr) Module._free(o_ptr);
   if (ev_ptr) Module._free(ev_ptr);

   // return PCA model and result objects
   return {
      class: ['pcamodel'],
      v: model,
      ncomp: ncomp,
      nrows: nrows,
      ncols: ncols,
      exclvars: exclvars,
      center: center,
      scale: scale
   };
}


/**
 * Projects dataset to a PCA model.
 *
 * @param {Object} Module object with wasm module for PCVPCA.
 * @param {Object} m JSON with PCA model created by `pca_fit()`.
 * @param {Matrix} X matrix with dataset.
 * @param {Array} outliers array with indices of outliers.
 *
 * @returns {Object} JSON with PCA results.
 */
export function pca_predict(Module, m, X, outliers) {


   if (!m || !m.class || !m.class.includes('pcamodel')) {
      throw new Error('pca_predict: parameter "m" must be JSON with PCA model.');
   }

   if (!ismatrix(X)) {
      throw new Error('pca_predict: X must be a matrix.');
   }

   // process outliers
   if (!outliers) outliers = [];
   const nout = outliers && Array.isArray(outliers) ? outliers.length : 0;
   if (!isArrCorrect(outliers, 0, X.nrows - 1)) {
      throw new Error('pca_predict: some indices in "outliers" array are outside the expected range.');
   }

   // excluded variables are taken from the model
   let nexv = m.exclvars.length;
   let exclvars = m.exclvars;

   // compute numbers of rows and columns without excluded ones
   const nrows = X.nrows - nout;

   // check if dataset alread excluded variables the ecluded variables array will be emptied
   if (X.ncols === m.ncols) {
      exclvars = [];
      nexv = 0;
   }

   // compute new number of columns
   const ncols = X.ncols - nexv;

   if (nrows < 1) {
      throw new Error('pca_predict: X must have at least 1 row.');
   }

   if (ncols !== m.ncols) {
      throw new Error('pca_predict: X must have the same number of columns (without excluded ones) as the training set.');
   }

   // allocate memory in WASM heap space and copy dataset values
   // here we send the full dataset including outliers and excluded variables
   const x_ptr = Module._malloc(X.v.length * X.v.BYTES_PER_ELEMENT);
   Module.HEAPF64.set(X.v, x_ptr / X.v.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space and copy outliers values
   let o_ptr = null;
   if (nout > 0) {
      const o = new Int32Array(outliers);
      o_ptr = Module._malloc(o.length * o.BYTES_PER_ELEMENT);
      Module.HEAP32.set(o, o_ptr / o.BYTES_PER_ELEMENT);
   }

   // allocate memory in WASM heap space and copy excluded variables values
   let ev_ptr = null;
   if (nexv > 0) {
      const ev = new Int32Array(m.exclvars);
      ev_ptr = Module._malloc(ev.length * ev.BYTES_PER_ELEMENT);
      Module.HEAP32.set(ev, ev_ptr / ev.BYTES_PER_ELEMENT);
   }

   // allocate memory in WASM heap space and copy model values
   const m_ptr = Module._malloc(m.v.length * m.v.BYTES_PER_ELEMENT);
   Module.HEAPF64.set(m.v, m_ptr / m.v.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space for result values
   const r_length = pca_getreslength(X.nrows - nout, m.ncomp, X.ncols - nexv);
   const r_constructor = Float64Array;
   const r_ptr = Module._malloc(r_length * r_constructor.BYTES_PER_ELEMENT);

   // call WASM function for PCA prediction
   const res = Module.ccall('pcapredict_wasm',
               ['number'], // return type
               ['number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'], // argument types
               [x_ptr, X.nrows, X.ncols, m_ptr, r_ptr, o_ptr, nout, ev_ptr, nexv]); // arguments

   if (res !== 1) {
      throw new Error('pca_predict: PCA prediction failed.');
   }

   // copy part of HEAP memory with result values
   const res_values = Module.HEAPF64.slice(
      r_ptr/r_constructor.BYTES_PER_ELEMENT,
      r_ptr/r_constructor.BYTES_PER_ELEMENT + r_length
   );

   // free the memory
   Module._free(x_ptr);
   Module._free(m_ptr);
   Module._free(r_ptr);
   if (o_ptr) Module._free(o_ptr);
   if (ev_ptr) Module._free(ev_ptr);

   // create object for PCA results
   const resobj = {
      class: ['pcares'],
      v: res_values,
      ncomp: m.ncomp,
      nrows: nrows,
      ncols: ncols,
      outliers: outliers,
      exclvars: m.exclvars,
      center: m.center,
      scale: m.scale
   };

   // compute object with statistics
   const expvar = new Array(m.ncomp);
   const resvar = new Array(m.ncomp);
   const cumresvar = new Array(m.ncomp);
   const cumexpvar = new Array(m.ncomp);
   const components = new Array(m.ncomp);

   const v = pcares_get(resobj, "xcumexpvar");
   components[0] = 1;
   cumexpvar[0] = v[0];
   cumresvar[0] = 1 - v[0];
   expvar[0] = v[0];
   resvar[0] = 1 - v[0];
   for (let i = 1; i < m.ncomp; i++) {
      components[i] = i + 1;
      cumexpvar[i] = v[i];
      cumresvar[i] = 1 - v[i];
      expvar[i] = v[i] - v[i - 1];
      resvar[i] = 1 - expvar[i];
   }

   // add statistics and return
   resobj.stat = {components, resvar, expvar, cumexpvar, cumresvar };
   return resobj;
}


/**
 * Create a PV-set based on PCA decomposition.
 *
 * @param {Object} Module object with wasm module for PCVPCA.
 * @param {Object} m JSON with PCA model created by `pca_fit()`.
 * @param {Matrix} X matrix with dataset.
 * @param {number} ncomp number of components for PV-set generation.
 * @param {number} cv_nseg number of segments for cross-validation.
 * @param {number} cv_type type of cross-validation (0: loo, 1: ven, 2: rand).
 * @param {number} cv_scope scope of cross-validation (0: global, 1: local).
 * @param {Array} outliers array with indices of outliers.
 *
 * @returns {Matrix} the generated PV-set.
 */
export function pcvpca(Module, m, X, ncomp, cv_nseg, cv_type, cv_scope, outliers) {

   if (!m || !m.class || !m.class.includes('pcamodel')) {
      throw new Error('pcapcv: parameter "m" must be JSON with PCA results.');
   }

   if (!ismatrix(X)) {
      throw new Error('pcapcv: X must be a matrix.');
   }

   // process outliers
   if (!outliers) outliers = [];
   const nout = outliers && Array.isArray(outliers) ? outliers.length : 0;
   if (!isArrCorrect(outliers, 0, X.nrows - 1)) {
      throw new Error('pca_predict: some indices in "outliers" array are outside the expected range.');
   }

   // excluded variables are taken from the model
   const nexv = m.exclvars.length;

   // compute numbers of rows and columns without excluded ones
   const nrows = X.nrows - nout;
   const ncols = X.ncols - nexv;


   if (nrows < 3) {
      throw new Error('pcapcv: X must have at least 3 rows.');
   }

   if (ncols !== m.ncols) {
      throw new Error('pcapcv: X must have the same number of columns as the training set.');
   }

   if (!Number.isInteger(cv_type) || ![0, 1, 2].includes(cv_type)) {
      throw new Error('pcapcv: parameter "cv_type" must be one of 0 ("loo"), 1 ("ven"), 2 ("rand).');
   }

   if (!Number.isInteger(cv_scope) || ![0, 1].includes(cv_scope)) {
      throw new Error('pcapcv: parameter "cv_scope" must be 0 ("global") or 1 ("local").');
   }

   if (cv_type > 0 && (!Number.isInteger(cv_nseg) || cv_nseg < 2)) {
      throw new Error('pcapcv: parameter "cv_nseg" must be a number ≥ 2.');
   }

   if (cv_type > 0 && cv_nseg > nrows) {
      throw new Error('pcapcv: the value of "cv_nseg" is too large for the given X.');
   }

   if (!Number.isInteger(ncomp) || ncomp < 1) {
      throw new Error('pcapcv: the value of "ncomp" must be a number >= 1.');
   }

   const max_ncomp = cv_type === 0 ?
      Math.min(nrows - 2, ncols, m.ncomp) :
      Math.min(nrows - Math.ceil(nrows / cv_nseg) - 1, ncols, m.ncomp);

   if (ncomp > max_ncomp) {
      throw new Error('pcapcv: the value of "ncomp" is too large.');
   }


   // allocate memory in WASM heap space and copy dataset values
   const x_ptr = Module._malloc(X.v.length * X.v.BYTES_PER_ELEMENT);
   Module.HEAPF64.set(X.v, x_ptr / X.v.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space and copy outliers values
   let o_ptr = null;
   if (nout > 0) {
      const o = new Int32Array(outliers);
      o_ptr = Module._malloc(o.length * o.BYTES_PER_ELEMENT);
      Module.HEAP32.set(o, o_ptr / o.BYTES_PER_ELEMENT);
   }

   // allocate memory in WASM heap space and copy excluded variables values
   let ev_ptr = null;
   if (nexv > 0) {
      const ev = new Int32Array(m.exclvars);
      ev_ptr = Module._malloc(ev.length * ev.BYTES_PER_ELEMENT);
      Module.HEAP32.set(ev, ev_ptr / ev.BYTES_PER_ELEMENT);
   }

   // allocate memory in WASM heap space and copy model values
   const m_ptr = Module._malloc(m.v.length * m.v.BYTES_PER_ELEMENT);
   Module.HEAPF64.set(m.v, m_ptr / m.v.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space for Xpv values
   const xpv_length = nrows * ncols;
   const xpv_constructor = X.v.constructor;
   const xpv_ptr = Module._malloc(xpv_length * xpv_constructor.BYTES_PER_ELEMENT);

   // call WASM function for PCAPCV
   const res = Module.ccall('pcvpca_wasm',
               ['number'], // return type
               ['number', 'number', 'number', 'number', 'number', 'number',
               'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'], // argument types
               [x_ptr, X.nrows, X.ncols, ncomp, m.center, m.scale,
               cv_type, cv_nseg, cv_scope, m_ptr, xpv_ptr, o_ptr, nout, ev_ptr, nexv]); // arguments

   if (res !== 1) {
      throw new Error('pcapcv: PCVPCA failed.');
   }

   // copy part of HEAP memory with Xpv values
   const xpv = Module.HEAPF64.slice(
      xpv_ptr/xpv_constructor.BYTES_PER_ELEMENT,
      xpv_ptr/xpv_constructor.BYTES_PER_ELEMENT + xpv_length
   );

   // free the memory
   Module._free(x_ptr);
   Module._free(m_ptr);
   Module._free(xpv_ptr);
   if (o_ptr) Module._free(o_ptr);
   if (ev_ptr) Module._free(ev_ptr);

   // return Xpv
   return new Matrix(xpv, nrows, ncols);
}
